import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Mail, CheckCircle, XCircle, SkipForward, RefreshCw, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_STYLES = {
  sent: { bg: "bg-green-100", text: "text-green-800", border: "border-green-200", icon: CheckCircle },
  failed: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200", icon: XCircle },
  skipped: { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-200", icon: SkipForward },
};

function formatDate(iso) {
  if (!iso) return "—";
  // Ensure UTC is recognised — append Z if no timezone info present
  const normalized = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
  const d = new Date(normalized);
  return d.toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/Dublin" }) +
    " " + d.toLocaleTimeString("en-IE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Dublin" });
}

export default function AdminEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [previewLog, setPreviewLog] = useState(null);
  const iframeRef = useRef(null);

  const fetchLogs = (s = search, st = statusFilter) => {
    setLoading(true);
    const params = {};
    if (s) params.search = s;
    if (st !== "all") params.status = st;
    api.getEmailLogs(params)
      .then((r) => { setLogs(r.data.logs); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLogs(); }, []);

  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => fetchLogs(val, statusFilter), 400));
  };

  const handleStatusChange = (st) => {
    setStatusFilter(st);
    fetchLogs(search, st);
  };

  const sentCount = logs.filter((l) => l.status === "sent").length;
  const failedCount = logs.filter((l) => l.status === "failed").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Email Log</h1>
          <p className="text-slate-500 mt-1">{total} emails logged (last 14 days)</p>
        </div>
        <Button variant="outline" onClick={() => fetchLogs()} className="gap-2" data-testid="refresh-email-logs">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-5">
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 text-center">
          <p className="text-green-800 font-bold text-lg">{sentCount}</p>
          <p className="text-green-600 text-xs font-medium">Sent</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-center">
          <p className="text-red-700 font-bold text-lg">{failedCount}</p>
          <p className="text-red-500 text-xs font-medium">Failed</p>
        </div>
      </div>

      {/* Search and filter */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by recipient or subject..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9 h-10"
            data-testid="email-log-search"
          />
        </div>
        <div className="flex gap-2">
          {["all", "sent", "failed", "skipped"].map((f) => (
            <button
              key={f}
              onClick={() => handleStatusChange(f)}
              className={`px-3 py-2 rounded-full text-xs font-medium transition-all ${
                statusFilter === f ? "bg-slate-700 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`email-log-filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden" data-testid="email-logs-table">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No email logs{search ? " matching your search" : ""}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-left px-5 py-3 font-semibold">Recipient(s)</th>
                  <th className="text-left px-5 py-3 font-semibold">Subject</th>
                  <th className="text-left px-5 py-3 font-semibold">Sent At</th>
                  <th className="text-left px-5 py-3 font-semibold">Error</th>
                  <th className="text-center px-3 py-3 font-semibold w-16">View</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => {
                  const s = STATUS_STYLES[log.status] || STATUS_STYLES.skipped;
                  const Icon = s.icon;
                  return (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"} data-testid={`email-log-row-${i}`}>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.bg} ${s.text} ${s.border}`}>
                          <Icon className="w-3 h-3" />
                          {log.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-700 max-w-xs truncate">
                        {(log.recipients || []).join(", ")}
                      </td>
                      <td className="px-5 py-3 text-slate-600 max-w-sm truncate">{log.subject}</td>
                      <td className="px-5 py-3 text-slate-500 whitespace-nowrap text-xs">{formatDate(log.sent_at)}</td>
                      <td className="px-5 py-3 text-red-500 text-xs max-w-xs truncate" title={log.error || ""}>
                        {log.error || "—"}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {log.html_body ? (
                          <button
                            onClick={() => setPreviewLog(log)}
                            className="text-slate-400 hover:text-green-700 transition-colors"
                            title="Preview email"
                            data-testid={`email-preview-btn-${i}`}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="text-slate-200" title="No preview available">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Email Preview Modal */}
      <Dialog open={!!previewLog} onOpenChange={(o) => !o && setPreviewLog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Email Preview
            </DialogTitle>
            {previewLog && (
              <div className="text-sm text-slate-500 space-y-0.5 mt-1">
                <p><strong>To:</strong> {(previewLog.recipients || []).join(", ")}</p>
                <p><strong>Subject:</strong> {previewLog.subject}</p>
                <p><strong>Sent:</strong> {formatDate(previewLog.sent_at)}</p>
              </div>
            )}
          </DialogHeader>
          <div className="flex-1 min-h-0 mt-3 border border-slate-200 rounded-xl overflow-hidden bg-white">
            {previewLog?.html_body && (
              <iframe
                ref={iframeRef}
                title="Email preview"
                srcDoc={previewLog.html_body}
                className="w-full h-full min-h-[400px] border-0"
                sandbox="allow-same-origin"
                data-testid="email-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
